import React, { Dispatch, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button, Grid, useTheme, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ArrowForward as ArrowForwardIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import EmployeeInfoCard from 'components/EmployeeInfoCard';
import EmploymentInfoCard from 'components/EmploymentInfoCard';
import IconCircle from 'components/IconCircle';
import NotesCard from 'components/NotesCard';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import { ROUTES } from 'shared/routes';
import Panel from './Panel';

const getCompleteStyles = (theme: Theme) => ({
  button: {
    margin: `${theme.spacing(2)} auto`,
  },
});

interface Props {
  resetState: () => void;
  setOpen: Dispatch<boolean>;
}

const CreateProcessModalPanelComplete: React.FC<Props> = (
  { resetState, setOpen }: Props,
) => {
  const theme = useTheme();
  const styles = getCompleteStyles(theme);
  const {
    process, resume, selectedNational,
  } = useContext(
    CreateProcessContext,
  );

  const handleNewProcessRequest = () => {
    resetState();
  };

  const button = (
    <Grid container alignItems="center">
      <Button
        sx={styles.button}
        endIcon={<ArrowForwardIcon />}
        component={Link}
        color="primary"
        to={ROUTES.processRequests}
        onClick={() => setOpen(false)}
      >
        MY PROCESS REQUESTS
      </Button>
    </Grid>
  );

  return (
    <Panel>
      <Typography align="center">
        View your submitted request below, or here:
      </Typography>

      {button}

      <Grid container alignItems="center">
        <Button
          onClick={handleNewProcessRequest}
          sx={styles.button}
          style={{ boxShadow: 'none', backgroundColor: 'transparent' }}
        >
          <Typography
            style={{ textDecoration: 'underline' }}
            component="p"
            variant="caption"
            align="center"
            color="primary"
          >
            START ANOTHER PROCESS REQUEST
            {'>'}
          </Typography>
        </Button>
      </Grid>

      <IconRow isHeader>
        <IconRowCell type="icon">
          <IconCircle Icon={InboxIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h4">New Process Request Details</Typography>
        </IconRowCell>
      </IconRow>

      <NotesCard />

      <EmployeeInfoCard
        process={process}
        national={
          process.foreignNationalID ? selectedNational : process.employeeData
        }
        resume={resume}
      />

      <EmploymentInfoCard />

      {button}
    </Panel>
  );
};

export default CreateProcessModalPanelComplete;
